import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import HeaderBar from "../../Components/UI/headerbar";
import { getWorkingdays, updateWorkingdays } from "../../MyApi/workingHours";

const WorkingHours = () => {
  const queryClient = useQueryClient();

  const [selectedDay, setSelectedDay] = useState(null);

  const [workingDays, setWorkingDays] = useState([]);

  const [tempTimes, setTempTimes] = useState({
    open_time: "",
    close_time: "",
  });

  const { data, isLoading, error } = useQuery(
    ["getWorkingdays"],
    getWorkingdays,
    {
      staleTime: 5 * 60 * 1000,
    },
  );

  useEffect(() => {
    if (data?.data?.working_days) {
      setWorkingDays(data.data.working_days);
    }
  }, [data]);

  const changedDays = useMemo(() => {
    if (!data?.data?.working_days) return [];

    return workingDays.filter((day) => {
      const original = data.data.working_days.find(
        (d) => d.day_of_week === day.day_of_week,
      );

      return (
        original?.open_time !== day.open_time ||
        original?.close_time !== day.close_time
      );
    });
  }, [workingDays, data]);

  const hasChanges = changedDays.length > 0;

  // mutation
  const updateMutation = useMutation(
    ({ id, formData }) => updateWorkingdays(formData, id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["getWorkingdays"]);
      },

      onError: (error) => {
        console.log(error);
      },
    },
  );

  // open modal
  const handleOpenModal = (day) => {
    setSelectedDay(day);
    setTempTimes({
      open_time: day.open_time,
      close_time: day.close_time,
    });
  };

  // apply changes locally
  const handleApplyChanges = () => {
    setWorkingDays((prev) =>
      prev.map((day) =>
        day.day_of_week === selectedDay.day_of_week
          ? {
              ...day,
              open_time: tempTimes.open_time,
              close_time: tempTimes.close_time,
            }
          : day,
      ),
    );

    setSelectedDay(null);
  };

  // save all changes
  const handleSaveAll = () => {
    const selectedStore = JSON.parse(localStorage.getItem("selectedStore"));

    const form = new FormData();

    form.append("_method", "put");

    // ✅ send all days (0 to 6), not just the changed ones
    workingDays.forEach((day, index) => {
      form.append(`working_days[${index}][day_of_week]`, day.day_of_week);
      form.append(`working_days[${index}][open_time]`, day.open_time);
      form.append(`working_days[${index}][close_time]`, day.close_time);
    });

    form.append("store_id", selectedStore?.id);

    updateMutation.mutate({
      id: selectedStore?.id,
      formData: form,
    });
  };

  if (isLoading) {
    return <p className="p-4">Loading...</p>;
  }

  if (error) {
    return <p className="p-4">Something went wrong</p>;
  }

  return (
    <div>
      <HeaderBar title={"ساعات العمل"} content={"إدارة ساعات العمل"} />

      <div className="container py-4">
        <div className="row g-4">
          {workingDays.map((day) => (
            <div className="col-md-6 m-0" key={day.day_of_week}>
              <div
                className="card border-0 shadow-sm rounded-4 p-3"
                style={{
                  cursor: "pointer",
                  transition: "0.2s",
                }}
                onClick={() => handleOpenModal(day)}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="fw-bold mb-1">{day.day_of_week_label}</h5>

                    <small className="text-muted">اضغط للتعديل</small>
                  </div>

                  <div className="text-end">
                    <div className="fw-bold text-primary">{day.open_time}</div>

                    <div className="fw-bold text-primary">{day.close_time}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {hasChanges && (
          <div
            className="position-fixed bottom-0 start-0 end-0 bg-white border-top shadow-lg p-3"
            style={{ zIndex: 1050 }}
          >
            <div className="container d-flex justify-content-between align-items-center">
              <div>لديك {changedDays.length} تغييرات غير محفوظة</div>

              <button
                className="btn btn-success px-4"
                onClick={handleSaveAll}
                disabled={updateMutation.isLoading}
              >
                {updateMutation.isLoading ? "جاري الحفظ..." : "حفظ التغييرات"}
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedDay && (
        <div
          className="modal fade show d-block"
          style={{
            background: "rgba(0,0,0,0.45)",
            backdropFilter: "blur(4px)",
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 rounded-4 overflow-hidden">
              <div className="p-4 border-bottom">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="fw-bold mb-1">
                      {selectedDay.day_of_week_label}
                    </h4>

                    <small className="text-muted">تعديل ساعات العمل</small>
                  </div>

                  <button
                    className="btn-close"
                    onClick={() => setSelectedDay(null)}
                  />
                </div>
              </div>

              <div className="p-4">
                <div className="mb-4">
                  <label className="form-label fw-semibold">وقت الفتح</label>

                  <input
                    type="time"
                    className="form-control form-control-lg rounded-3"
                    value={tempTimes.open_time}
                    onChange={(e) =>
                      setTempTimes({
                        ...tempTimes,
                        open_time: e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <label className="form-label fw-semibold">وقت الإغلاق</label>

                  <input
                    type="time"
                    className="form-control form-control-lg rounded-3"
                    value={tempTimes.close_time}
                    onChange={(e) =>
                      setTempTimes({
                        ...tempTimes,
                        close_time: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="p-4 border-top d-flex gap-2">
                <button
                  className="btn btn-light flex-fill"
                  onClick={() => setSelectedDay(null)}
                >
                  إلغاء
                </button>

                <button
                  className="btn btn-primary flex-fill"
                  onClick={handleApplyChanges}
                >
                  تطبيق
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkingHours;